import { useAbility } from "@casl/react";
import { useStaff } from "@gymflow/api";
import { cn } from "@gymflow/helpers";
import classNames from "classnames";
import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import { Tooltip } from "react-tooltip";

import searchIcon from "../../../assets/img/search-lg.svg";
import { usePortalRoutes } from "../../hooks/usePortalRoutes";
import { AbilityContext, Can, Subject, Verb } from "../../permissions";
import { AuthenticationContext } from "../../providers/authentication/AuthenticationContext";
import useGymflowModels from "../../store";
import { UserMemberPage } from "../../views/UserMember";
import { Button, MarkerPinIcon, NavigateToClubSelect } from "../atoms";
import { NewUserSideBarProviderContext } from "../molecules";
import { SearchGlobal } from "../SearchGlobal/SearchGlobal";
import { ShopSidebarContext } from "../Shop/ShopSidebar/ShopSidebarContext";
import addUser from "./../../../assets/img/add-user.svg";
import calendarIcon from "./../../../assets/img/calendar.svg";
import shopIcon from "./../../../assets/img/shop-icon.svg";
import { CalendarSideBar } from "./Notification/CalendarSideBar";

export interface TopNavbarContentsProps {
  navigationIconOnly?: boolean;
}

export const TopNavbarContents: React.FC<TopNavbarContentsProps> = ({
  navigationIconOnly,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { createMemberLink, createLeadLink } = usePortalRoutes();
  const [areEventsOpened, setAreEventsOpened] = useState(false);
  const { open: openNewUserSideBar } = useContext(
    NewUserSideBarProviderContext,
  );
  const { showShop } = useContext(ShopSidebarContext);
  const [searchClubSelectorToggle, setSearchClubSelectorToggle] =
    useState(false);

  const { api } = useGymflowModels();
  const { userId: id } = useContext(AuthenticationContext);
  const { data: currentStaff } = useStaff({ api, staffId: id });
  const showPicker =
    currentStaff && currentStaff?.accessibleClubIds.length > 1;

  const ability = useAbility(AbilityContext);
  return (
    <>
      {(!navigationIconOnly || !searchClubSelectorToggle) && (
        <div className="flex size-full flex-row justify-between">
          <Can I={Verb.View} a={Subject.GlobalSearch}>
            <SearchGlobal />
          </Can>
        </div>
      )}
      {showPicker && (!navigationIconOnly || searchClubSelectorToggle) && (
        <div
          className={classNames("h-full lg:min-w-fit", {
            "w-full": searchClubSelectorToggle,
          })}
        >
          <NavigateToClubSelect />
        </div>
      )}

      <div className="flex h-full flex-row">
        {showPicker && navigationIconOnly && (
          <>
            <div
              data-tooltip-id="club-or-search-tooltip"
              className={classNames(
                "flex h-full w-14 cursor-pointer items-center justify-center border-l border-gray-300 hover:bg-gray-100",
              )}
              onClick={() => {
                setSearchClubSelectorToggle((e) => !e);
              }}
            >
              {!searchClubSelectorToggle ? (
                <MarkerPinIcon className="stroke-primary-700 size-6" />
              ) : (
                <img src={searchIcon} alt="Search Icon" />
              )}
            </div>
            <Tooltip
              className="z-50 rounded-lg !bg-black !text-xs !font-semibold"
              id="club-or-search-tooltip"
              content={
                searchClubSelectorToggle
                  ? t("components.navbars.topNavbarContents.tooltip.search")
                  : t("components.navbars.topNavbarContents.tooltip.clubs")
              }
            />
          </>
        )}
        <div
          className={classNames(
            "flex h-full w-14 cursor-pointer items-center justify-center border-l border-gray-300 hover:bg-gray-100",
          )}
          onClick={() => {
            setAreEventsOpened(true);
          }}
          data-tooltip-id="calendar-tooltip"
        >
          <img src={calendarIcon} alt="Calendar Icon" />
        </div>
        <Tooltip
          className="z-50 rounded-lg !bg-black !text-xs !font-semibold"
          id="calendar-tooltip"
          content={t("components.navbars.topNavbarContents.tooltip.calendar")}
        />
        <Can I={Verb.View} a={Subject.Sales}>
          <Button
            className={cn(
              "flex h-full w-14 cursor-pointer items-center justify-center !rounded-none !border-l border-b-0 border-r-0 border-t-0 border-gray-300 !bg-gray-0 !ring-0 hover:!bg-gray-100 focus:outline-none p-0",
            )}
            onClick={async () => {
              await showShop();
            }}
            data-tooltip-id="sell-tooltip"
          >
            <img src={shopIcon} alt="Shop Icon" />
          </Button>
          <Tooltip
            className="z-50 rounded-lg !bg-black !text-xs !font-semibold"
            id="sell-tooltip"
            content={t("components.navbars.topNavbarContents.tooltip.sell")}
          />
        </Can>
        <div
          className={classNames(
            "flex h-full w-14 cursor-pointer items-center justify-center border-l border-gray-300 hover:bg-gray-100",
            { hidden: !ability.can(Verb.View, Subject.Sales) },
          )}
          onClick={() => {
            openNewUserSideBar({
              onClose: async (arg) => {
                if (arg?.userType === "MEMBER") {
                  history.push(
                    createMemberLink(arg.userMemberId, UserMemberPage.Profile),
                  );
                } else if (arg?.userType === "LEAD") {
                  history.push(createLeadLink(arg.leadId));
                }
              },
            });
          }}
          data-tooltip-id="create-new-user-tooltip"
        >
          <img src={addUser} alt="Add User Icon" />
        </div>
        <Tooltip
          className="z-50 rounded-lg !bg-black !text-xs !font-semibold"
          id="create-new-user-tooltip"
          content={t(
            "components.navbars.topNavbarContents.tooltip.createNewUser",
          )}
        />
        <CalendarSideBar
          isCalendarOpened={areEventsOpened}
          hide={() => setAreEventsOpened(false)}
        />
      </div>
    </>
  );
};
